/**
 * Team/TeamPublic.jsx
 * ════════════════════════════════════════════════════════════════
 * Публічна сторінка "Команда" садочка.
 *
 * Props:
 *   kgId  {string}
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faTh, faList } from '@fortawesome/free-solid-svg-icons';
import { constr_uri } from '../../../../../services/apiClient';
import apiClient from '../../../../../services/apiClient';
import TeamGallery from './TeamGallery';

// ── Константи ─────────────────────────────────────────────────────────────────

var CARD_CLOSED_HEIGHT = 220; // px — висота закритої картки
var PHOTO_CLOSED_W    = 140; // px
var PHOTO_OPEN_W      = 170; // px
var PHOTO_PADDING     = 12;  // px — відступи навколо фото

var GROUP_TAGS = {
    1: { label: 'Керівництво',          bg: '#e6f1fb', color: '#0c447c' },
    2: { label: 'Вихователі та педагоги', bg: '#e1f5ee', color: '#085041' },
    3: { label: 'Інші',                  bg: '#f1efe8', color: '#444441' },
};

// ── Стилі — один раз ─────────────────────────────────────────────────────────

var STYLES_INJECTED = false;
function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = [
        '@keyframes team-card-open  { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }',
        '.team-card { transition: box-shadow 0.2s, border-color 0.2s; }',
        '.team-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.09) !important; }',
        '.team-card-photo { transition: width 0.35s ease, height 0.35s ease, border-radius 0.35s ease; }',
        '.team-card-extra { animation: team-card-open 0.3s ease both; }',
    ].join('\n');
    document.head.appendChild(s);
}

// ── API ───────────────────────────────────────────────────────────────────────

var publicTeamAPI = {
    getSettings: async function(kgId) {
        try {
            var r = await apiClient.get('public/team-settings/' + kgId);
            return r.data || {};
        } catch(e) { return {}; }
    },
    getMembers: async function(kgId) {
        try {
            var r = await apiClient.get('public/employees/' + kgId + '/team');
            return r.data || [];
        } catch(e) { return []; }
    },
};

// ── Одна картка ───────────────────────────────────────────────────────────────

var TeamCard = function(props) {
    var m       = props.member;
    var index   = props.index   || 0;
    var onOpen  = props.onOpen  || null;
    var onClose = props.onClose || null;

    var [open, setOpen]   = useState(false);
    var contentRef        = useRef(null);
    var cardRef           = useRef(null);

    var avatarSrc = m.photoDocId && m.avatarFileId
        ? constr_uri('docfile?doc_id=' + m.photoDocId + '&file_id=' + m.avatarFileId)
        : null;

    // Картка без фото не показується (перевірка на рівні батька)
    if (!avatarSrc) return null;

    var tag = GROUP_TAGS[m.displayGroup] || GROUP_TAGS[3];

    var toggleOpen = function(e) {
        if (e.target.tagName === 'A') return;
        if (open) {
            setOpen(false);
            onClose && onClose(index, cardRef);
        } else {
            setOpen(true);
            onOpen && onOpen(index, cardRef);
        }
    };

    return (
        <div
            ref={cardRef}
            className="team-card"
            onClick={toggleOpen}
            style={{
                border: open ? '1px solid #86b7fe' : '1px solid #e5e7eb',
                borderRadius: 14,
                background: '#fff',
                overflow: 'hidden',
                cursor: 'pointer',
                boxShadow: open ? '0 4px 20px rgba(13,110,253,0.1)' : '0 1px 4px rgba(0,0,0,0.05)',
                transition: 'box-shadow 0.2s, border-color 0.2s',
                userSelect: 'none',
            }}
        >
            {/* ── Верхній рядок: фото + інфо ── */}
            <div style={{
                display: 'flex',
                alignItems: 'stretch',
                minHeight: CARD_CLOSED_HEIGHT,
            }}>
                {/* Фото */}
                <div style={{
                    flexShrink: 0,
                    width: open ? PHOTO_OPEN_W + PHOTO_PADDING * 2 : PHOTO_CLOSED_W + PHOTO_PADDING * 2,
                    transition: 'width 0.35s ease',
                    padding: PHOTO_PADDING,
                    background: '#f9fafb',
                    borderRight: '1px solid #f0f0f0',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'center',
                }}>
                    <div style={{
                        width:  open ? PHOTO_OPEN_W : PHOTO_CLOSED_W,
                        height: open ? Math.round(PHOTO_OPEN_W * 4/3) : Math.round(PHOTO_CLOSED_W * 4/3),
                        borderRadius: 10,
                        overflow: 'hidden',
                        position: 'relative',
                        transition: 'width 0.35s ease, height 0.35s ease',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    }}>
                        <img
                            src={avatarSrc}
                            alt={m.fullName}
                            style={{
                                position: 'absolute', inset: 0,
                                width: '100%', height: '100%',
                                objectFit: 'cover',
                                objectPosition: 'top center',
                                transition: 'transform 0.35s ease',
                                transform: open ? 'scale(1.05)' : 'scale(1)',
                            }}
                        />
                    </div>
                </div>

                {/* Права колонка — ПІБ/посада/тег внизу */}
                <div style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '16px 20px',
                    minWidth: 0,
                    position: 'relative',
                    justifyContent: open ? 'flex-end' : 'space-between',
                }}>
                    {/* Закритий стан — bio preview зверху */}
                    {!open && m.bio && (
                        <div style={{ flex: 1, position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
                            <div
                                style={{
                                    fontSize: 13, lineHeight: 1.7,
                                    color: '#495057',
                                    maxHeight: CARD_CLOSED_HEIGHT - 110,
                                    overflow: 'hidden',
                                }}
                                dangerouslySetInnerHTML={{ __html: m.bio }}
                            />
                            <div style={{
                                position: 'absolute', bottom: 0, left: 0, right: 0,
                                height: 48,
                                background: 'linear-gradient(transparent, #fff)',
                            }} />
                        </div>
                    )}

                    {/* ПІБ + посада + тег — завжди внизу правої колонки */}
                    <div>
                        <div style={{
                            fontSize: 17, fontWeight: 700,
                            color: '#111827', marginBottom: 3, lineHeight: 1.3,
                        }}>
                            {m.fullName}
                        </div>
                        <div style={{
                            fontSize: 13, color: '#0069ff', fontWeight: 600,
                            marginBottom: 8,
                            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                        }}>
                            <span>{m.position || '—'}</span>
                            {m.age && (
                                <span style={{
                                    fontSize: 12, color: '#9ca3af', fontWeight: 400,
                                    borderLeft: '1px solid #e5e7eb', paddingLeft: 8,
                                }}>
                                    {m.age} р.
                                </span>
                            )}
                        </div>
                        <span style={{
                            display: 'inline-block',
                            background: tag.bg, color: tag.color,
                            padding: '2px 10px', borderRadius: 10,
                            fontSize: 11, fontWeight: 700,
                        }}>
                            {tag.label}
                        </span>
                    </div>

                    {/* Кнопка "Читати далі" в закритому стані */}
                    {!open && (
                        <div style={{
                            position: 'absolute', bottom: 14, right: 16,
                            display: 'flex', alignItems: 'center', gap: 4,
                            fontSize: 12, color: '#0069ff', fontWeight: 600,
                        }}>
                            Читати далі
                            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 10 }} />
                        </div>
                    )}
                </div>
            </div>

            {/* ── Повний текст на всю ширину (тільки відкритий стан) ── */}
            {open && m.bio && (
                <div
                    ref={contentRef}
                    className="team-card-extra"
                    style={{
                        borderTop: '1px solid #f0f0f0',
                        padding: '16px 20px 14px',
                    }}
                >
                    <div
                        onClick={function(e) { e.stopPropagation(); }}
                        style={{ fontSize: 14, lineHeight: 1.75, color: '#374151' }}
                        dangerouslySetInnerHTML={{ __html: m.bio }}
                    />
                    <div style={{
                        display: 'flex', justifyContent: 'flex-end',
                        marginTop: 12,
                    }}>
                        <div
                            onClick={function(e) { e.stopPropagation(); setOpen(false); }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                fontSize: 12, color: '#6b7280', fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Згорнути
                            <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: 10 }} />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ════════════════════════════════════════════════════════════════
// ГОЛОВНИЙ КОМПОНЕНТ
// ════════════════════════════════════════════════════════════════

var TeamPublic = function(props) {
    var kgId = props.kgId;

    var [members, setMembers]           = useState([]);
    var [description, setDescription]   = useState('');
    var [loading, setLoading]           = useState(true);
    var [descOpen, setDescOpen] = useState(false);
    var descRef                 = useRef(null);
    var descBlockRef            = useRef(null);
    var [viewMode, setViewMode] = useState('gallery'); // 'gallery' | 'cards'

    var handleDescToggle = function() {
        var opening = !descOpen;
        setDescOpen(opening);
        setTimeout(function() {
            if (!descBlockRef.current) return;
            var rect = descBlockRef.current.getBoundingClientRect();
            var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            var offset = 80;
            if (opening) {
                // При розкритті — скролимо до плашки
                window.scrollTo({ top: scrollTop + rect.top - offset, behavior: 'smooth' });
            } else {
                // При закритті — якщо вийшла за верх екрану — повертаємось
                if (rect.top < offset) {
                    window.scrollTo({ top: scrollTop + rect.top - offset, behavior: 'smooth' });
                }
            }
        }, 50);
    };

    useEffect(function() {
        if (!kgId) return;
        Promise.all([
            publicTeamAPI.getSettings(kgId),
            publicTeamAPI.getMembers(kgId),
        ]).then(function(results) {
            setDescription(results[0].description || '');
            setMembers(results[1]);
        }).catch(function() {}).finally(function() {
            setLoading(false);
        });
    }, [kgId]);


    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spinner animation="border" variant="primary" size="sm" />
            </div>
        );
    }

    // Показуємо тільки тих у кого є фото
    var visible = members.filter(function(m) { return m.photoDocId && m.avatarFileId; });

    if (visible.length === 0) return null;

    return (
        <div>
            {/* ── Переключач режиму ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <div style={{
                    display: 'flex',
                    border: '1px solid #e5e7eb',
                    borderRadius: 8, overflow: 'hidden',
                    background: '#fff',
                }}>
                    {[
                        { key: 'gallery', icon: faTh,   title: 'Галерея' },
                        { key: 'cards',   icon: faList,  title: 'Картки' },
                    ].map(function(btn) {
                        var isActive = viewMode === btn.key;
                        return (
                            <button
                                key={btn.key}
                                type="button"
                                onClick={function() { setViewMode(btn.key); }}
                                title={btn.title}
                                style={{
                                    width: 36, height: 32, border: 'none',
                                    background: isActive ? '#0069ff' : 'transparent',
                                    color: isActive ? '#fff' : '#9ca3af',
                                    cursor: 'pointer', fontSize: 14,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    transition: 'background 0.15s, color 0.15s',
                                }}
                            >
                                <FontAwesomeIcon icon={btn.icon} />
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Вміст залежно від режиму ── */}
            {viewMode === 'gallery' ? (
                <TeamGallery
                    members={visible}
                    description={description}
                    descOpen={descOpen}
                    onDescToggle={handleDescToggle}
                    cols={5}
                />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {visible.map(function(member, idx) {
                        return (
                            <TeamCard
                                key={member.id}
                                member={member}
                                index={idx}
                                onOpen={function(i, cardRef) {
                                    setTimeout(function() {
                                        if (!cardRef.current) return;
                                        var rect = cardRef.current.getBoundingClientRect();
                                        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                        window.scrollTo({ top: scrollTop + rect.top - 80, behavior: 'smooth' });
                                    }, 50);
                                }}
                                onClose={function(i, cardRef) {
                                    setTimeout(function() {
                                        if (!cardRef.current) return;
                                        var rect = cardRef.current.getBoundingClientRect();
                                        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                                        if (rect.top < 80) {
                                            window.scrollTo({ top: scrollTop + rect.top - 80, behavior: 'smooth' });
                                        }
                                    }, 50);
                                }}
                            />
                        );
                    })}

                    {/* Опис в режимі карточок */}
                    {description && (
                        <div
                            ref={descBlockRef}
                            style={{
                                background: 'linear-gradient(135deg, #f0f7ff 0%, #e8f5e9 100%)',
                                borderRadius: 14, padding: '20px 24px', marginTop: 8,
                                border: '1px solid rgba(0,105,255,0.08)',
                            }}
                        >
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                                Наша команда
                            </div>
                            <div style={{ position: 'relative' }}>
                                <div
                                    ref={descRef}
                                    style={{ fontSize: 15, lineHeight: 1.7, color: '#374151', maxHeight: descOpen ? 'none' : CARD_CLOSED_HEIGHT, overflow: 'hidden' }}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                />
                                {!descOpen && (
                                    <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 56, background: 'linear-gradient(transparent, #e8f5e9)', pointerEvents: 'none' }} />
                                )}
                            </div>
                            <div style={{ marginTop: descOpen ? 12 : 4, textAlign: 'center' }}>
                                <button type="button" onClick={handleDescToggle} style={{ background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(0,105,255,0.2)', borderRadius: 20, padding: '4px 16px', fontSize: 12, color: '#0069ff', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                    <FontAwesomeIcon icon={descOpen ? faChevronUp : faChevronDown} style={{ fontSize: 10 }} />
                                    {descOpen ? 'Згорнути' : 'Читати далі'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TeamPublic;